import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import fdProducts from "@config/fd-products.json";
import rdProducts from "@config/rd-products.json";
import dailyProducts from "@config/daily-products.json";
import goldProducts from "@config/gold-products.json";
import siteContent from "@config/site-content.json";
import contact from "@config/contact.json";
import ProductGrid from "../components/ProductGrid.jsx";
import SectionTitle from "../components/SectionTitle.jsx";
import { productStats } from "../lib/site.js";

export default function Home({ t, branding }) {
  const stats = [
    ["FD Products", productStats(fdProducts).bestRate.toFixed(2) + "%"],
    ["RD Products", productStats(rdProducts).bestRate.toFixed(2) + "%"],
    ["Daily Plans", dailyProducts.length],
    ["Gold Loan LTV", Math.max(...goldProducts.map((item) => item.loanToValuePercent)) + "%"]
  ];

  return (
    <>
      <section className="relative overflow-hidden" style={{ background: "var(--hero-gradient)" }}>
        <div className="section-shell grid items-center gap-10 py-16 lg:grid-cols-[1.05fr_0.95fr]">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}>
            <p className="mb-4 w-fit rounded-full bg-white px-4 py-2 text-sm font-black text-maroon shadow-premium">{branding.tagline}</p>
            <h1 className="max-w-4xl text-4xl font-black leading-tight text-ink md:text-6xl">{t.home.heroTitle}</h1>
            <p className="mt-5 max-w-2xl text-lg leading-8 text-muted">{t.home.heroText}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link className="focus-ring rounded-sur bg-royal px-5 py-3 font-black text-white" to="/fixed-deposits">{t.actions.enquire}</Link>
              <Link className="focus-ring rounded-sur border border-royal bg-white px-5 py-3 font-black text-royal" to="/downloads">{t.actions.download}</Link>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="glass rounded-sur p-5">
            <div className="rounded-sur bg-white p-6">
              <img src={branding.logo.primary} alt={branding.logo.alt} className="mb-6 h-16" />
              <div className="grid gap-4 sm:grid-cols-2">
                {stats.map(([label, value]) => (
                  <div key={label} className="rounded-sur border border-[var(--line)] bg-ivory p-5">
                    <span className="text-sm font-bold text-muted">{label}</span>
                    <strong className="mt-1 block text-3xl text-maroon">{value}</strong>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

            <section className="bg-white py-16">
        <div className="section-shell">
          <SectionTitle eyebrow={t.home.about} title={branding.legalName} text={siteContent.about.overview} />
          <div className="grid gap-5 md:grid-cols-3">
            {["vision", "mission", "values"].map((key) => (
              <article key={key} className="premium-card p-5">
                <h2 className="mb-3 text-xl font-black capitalize text-royal">{key}</h2>
                <p className="leading-7 text-muted">{Array.isArray(siteContent.about[key]) ? siteContent.about[key].join(" • ") : siteContent.about[key]}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <ProductBand title={t.sections.fd} products={fdProducts.slice(0, 4)} to="/fixed-deposits" t={t} />
      <ProductBand title={t.sections.rd} products={rdProducts} to="/recurring-deposits" t={t} />
      <ProductBand title={t.sections.daily} products={dailyProducts} to="/daily-collection" t={t} />
      <ProductBand title={t.sections.gold} products={goldProducts} to="/gold-loan" t={t} />

      <section className="bg-white py-16">
        <div className="section-shell">
          <SectionTitle eyebrow={t.home.why} title="Trust, convenience, and future-ready service" />
          <div className="grid gap-5 md:grid-cols-4">
            {siteContent.whyChoose.map((item) => (
              <article key={item.title} className="premium-card p-5">
                <h2 className="text-lg font-black text-royal">{item.title}</h2>
                <p className="mt-3 text-sm leading-6 text-muted">{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section-shell grid gap-5 py-16 md:grid-cols-3">
        <div className="premium-card p-6 md:col-span-2">
          <SectionTitle eyebrow={t.home.ai} title="Rule-based assistant powered by local knowledge files" text="The assistant searches markdown content from the knowledge folder and answers common member questions without external AI APIs." />
        </div>
        <div className="premium-card p-6">
          <SectionTitle eyebrow={t.home.contact} title={contact.phone} text={`${contact.email} • ${contact.officeHours}`} />
        </div>
      </section>

      
    </>
  );
}

function ProductBand({ title, products, to, t }) {
  return (
    <section className="section-shell py-16">
      <div className="mb-8 flex items-end justify-between gap-4">
        <SectionTitle title={title} />
        <Link className="hidden rounded-sur border border-royal px-4 py-2 text-sm font-black text-royal md:block" to={to}>View all</Link>
      </div>
      <ProductGrid products={products} cta={t.actions.enquire} onSelect={() => { window.location.href = to; }} />
    </section>
  );
}
